import React from 'react';
import { useWatch } from 'react-hook-form';
import { CountryFlagAdornment } from '@components/moleculs/country-flag-adornment';
import { CountryOption } from '@components/moleculs/country-option';
import { FormAutocomplete } from '@components/organisms/forms/form-autocomplete';
import type { FormCountryAutocompleteProps } from '@components/organisms/forms/form-country-autocomplete/types';
import { useCountryOptions } from '@hooks/options/use-country-options';
import type { AutocompleteOption } from '@models/types';

// RHF form-bound single-select country picker: injects the flag option, the input flag adornment
// and by-value equality, so consumers pass only `name` (options default to the shared list).
// Replaces the `useCountryAutocomplete` hook wiring in the plain FormAutocomplete call sites.
export const FormCountryAutocomplete = ({
    name,
    options,
    textFieldProps,
    autocompleteProps,
    ...rest
}: FormCountryAutocompleteProps) => {
    const countryOptions = useCountryOptions();
    const value = useWatch<Record<string, AutocompleteOption>>({ name });
    // Small sizes get the compact option layout; standard sizes keep MUI's roomy default.
    const inputSize = textFieldProps?.inputSize ?? 'm';
    const compact = inputSize === 's' || inputSize === 's/m';

    return (
        <FormAutocomplete<AutocompleteOption, false, boolean, false>
            name={name}
            options={options ?? countryOptions}
            {...rest}
            textFieldProps={{
                ...textFieldProps,
                InputProps: {
                    ...textFieldProps?.InputProps,
                    startAdornment: <CountryFlagAdornment countryCode={value?.value} />,
                },
            }}
            autocompleteProps={{
                isOptionEqualToValue: (option, selected) => option?.value === selected?.value,
                ...autocompleteProps,
                renderOption: ({ key, className, ...optionProps }, option) => (
                    <CountryOption
                        key={key}
                        className={className}
                        isShowCheckbox={false}
                        compact={compact}
                        option={option}
                        {...optionProps}
                    />
                ),
            }}
        />
    );
};
